// src/components/ServiceCard.tsx

import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Lock } from "lucide-react";

const CardContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;

  &.enabled {
    cursor: pointer;

    &:hover {
      transform: translateY(-10px) scale(1.03);
      box-shadow: 0 20px 40px rgba(74, 144, 226, 0.18);
      border-color: transparent;
    }

    &:hover::before {
      transform: scaleX(1);
      transform-origin: left;
    }
  }

  &.disabled {
    opacity: 0.55;
    cursor: not-allowed;
    pointer-events: none;
    background: #f8fafc;

    &::before {
      background: #cbd5e1;
    }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #4a90e2, #9010d8);
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.4s ease;
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #4a90e2, #9010d8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: white;
  box-shadow: 0 8px 20px rgba(74, 144, 226, 0.25);
  transition: all 0.3s ease;

  svg {
    width: 40px;
    height: 40px;
  }

  &.disabled {
    filter: grayscale(0.7);
    opacity: 0.85;
    background: linear-gradient(135deg, #cbd5e1, #94a3b8);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 12px;
  letter-spacing: -0.2px;
`;

const CardDescription = styled.p`
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.7;
  max-width: 280px;
  margin: 0;
`;

const DisabledOverlay = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  color: #94a3b8;
  opacity: 0.7;
`;

interface ServiceCardProps {
  title: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  title,
  description,
  path,
  icon,
  disabled = false,
}) => {
  const isEnabled = !disabled;

  const titleAttr = disabled
    ? "دسترسی محدود شده — فقط نقش مجاز می‌تواند استفاده کند"
    : title;

  return (
    <CardContainer
      className={isEnabled ? "enabled" : "disabled"}
      title={titleAttr}
      aria-disabled={disabled}
    >
      {disabled && (
        <DisabledOverlay>
          <Lock size={20} />
        </DisabledOverlay>
      )}

      <IconWrapper className={disabled ? "disabled" : ""}>{icon}</IconWrapper>

      <CardTitle>{title}</CardTitle>
      <CardDescription>{description}</CardDescription>

      {isEnabled && (
        <Link
          to={path}
          style={{ position: "absolute", inset: 0, zIndex: 1 }}
          aria-label={`رفتن به ${title}`}
        />
      )}
    </CardContainer>
  );
};

export default ServiceCard;
